/**
 * TAILWIND PAGE HEADER KOMPONENTEN
 *
 * Exportiert 2 Komponenten:
 *
 * --- TWPageHeader (einfacher Header ohne Tabs) ---
 *   title          - string, Seitentitel (h1)
 *   description?   - string, Untertitel
 *   actions?       - ReactNode, Buttons rechts (Mobile: darunter)
 *   noBorder?      - boolean (default: false), Trennlinie ausblenden
 *   centerActions? - boolean (default: false), Actions zentrieren statt rechts
 *   className?     - zusätzliche Klassen
 *
 * --- TWPageHeaderTabs (Header mit Tab-Navigation) ---
 *   title               - string, Seitentitel
 *   description?        - string, Untertitel
 *   tabs                - Array von { id, name, href?, current?, badge?, icon? }
 *   onTabChange?        - (tabId: string) => void, Callback bei Tab-Wechsel
 *   actions?            - ReactNode, Buttons rechts
 *   breadcrumbs?        - Array von { label, href? }
 *   accentColor?        - orange (default) | gold (Brand-Farbe)
 *   hideActionsOnMobile? - boolean (default: false)
 *   centerActions?      - boolean (default: false)
 *   className?          - zusätzliche Klassen
 *
 * Usage:
 *   <TWPageHeader title="Blog" actions={<SearchInput />} />
 *
 *   <TWPageHeaderTabs
 *     title="Termine"
 *     tabs={[
 *       { id: 'meine', name: 'Meine Termine', icon: Calendar, current: true },
 *       { id: 'buchen', name: 'Termin buchen', icon: CalendarPlus },
 *     ]}
 *     onTabChange={(tabId) => setActiveTab(tabId)}
 *     accentColor="gold"
 *   />
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDown, ChevronRight, Check } from 'lucide-react';

// ============================================
// TYPES
// ============================================
export interface Tab {
  /** Eindeutige ID für den Tab */
  id: string;
  /** Anzeigename */
  name: string;
  /** Optional: Link-URL (wenn nicht gesetzt, wird onTabChange verwendet) */
  href?: string;
  /** Ist dieser Tab aktiv? */
  current?: boolean;
  /** Optional: Badge-Zähler */
  badge?: number;
  /** Optional: Icon-Komponente (z.B. aus lucide-react) */
  icon?: React.ComponentType<{ className?: string }>;
}

export interface Breadcrumb {
  label: string;
  href?: string;
}

/**
 * Meta-Item fuer Info-Zeile unter dem Titel (z.B. Status, Datum, Autor mit Icon).
 * Nach Tailwind-Plus-Vorlage headings/page-headings/05-with-meta-and-actions.jsx.
 */
export interface MetaItem {
  /** Optionales Icon (lucide-react Komponente oder kompatibel) */
  icon?: React.ComponentType<{ className?: string }>;
  /** Anzeigetext */
  label: string;
  /**
   * Optionale Textfarbe als Tailwind-Klasse (z.B. 'text-status-success',
   * 'text-red-500'). Wird auf Text UND Icon angewendet. Default: gedecktes Grau.
   */
  colorClass?: string;
}

/**
 * Rendert eine Reihe von Meta-Items (Icon + Label) unter dem Titel.
 * Wird von TWPageHeader und TWPageHeaderTabs intern verwendet.
 */
function MetaRow({ items }: { items: MetaItem[] }) {
  if (items.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
      {items.map((item, idx) => {
        const Icon = item.icon;
        const color = item.colorClass || 'text-gray-500 dark:text-gray-400';
        return (
          <span key={idx} className={`inline-flex items-center gap-1.5 text-sm ${color}`}>
            {Icon && <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />}
            <span>{item.label}</span>
          </span>
        );
      })}
    </div>
  );
}

/**
 * Rendert eine Breadcrumb-Navigation oberhalb des Titels.
 * Wird von TWPageHeader und TWPageHeaderTabs intern verwendet.
 */
function BreadcrumbRow({ items }: { items: Breadcrumb[] }) {
  if (items.length === 0) return null;
  return (
    <nav className="flex mb-4" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        {items.map((crumb, index) => (
          <li key={crumb.label} className="flex items-center">
            {index > 0 && (
              <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500 mx-1 shrink-0" />
            )}
            {crumb.href ? (
              <Link
                to={crumb.href}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors"
              >
                {crumb.label}
              </Link>
            ) : (
              <span className="text-sm text-gray-700 dark:text-gray-300">{crumb.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

// ============================================
// TWPageHeader - EINFACHER HEADER (ohne Tabs)
// ============================================
export interface TWPageHeaderProps {
  /** Seitentitel */
  title: string;
  /** Optionale Beschreibung */
  description?: string;
  /** Optionale Aktions-Buttons (rechts vom Titel, auf Mobile darunter) */
  actions?: React.ReactNode;
  /** Optionale Breadcrumb-Navigation oberhalb des Titels */
  breadcrumbs?: Breadcrumb[];
  /** Optionale Meta-Info-Zeile unter dem Titel (Status, Datum, Autor mit Icons) */
  meta?: MetaItem[];
  /** Extra CSS-Klassen */
  className?: string;
  /** Trennlinie ausblenden (Standard: false = Trennlinie sichtbar) */
  noBorder?: boolean;
  /** Actions zentrieren statt rechts ausrichten (Standard: false) */
  centerActions?: boolean;
}

export function TWPageHeader({
  title,
  description,
  actions,
  breadcrumbs,
  meta,
  className = '',
  noBorder = false,
  centerActions = false,
}: TWPageHeaderProps) {
  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;
  const hasMeta = meta && meta.length > 0;

  return (
    <div
      className={`
        pt-5 pb-5
        ${!noBorder ? 'border-b border-gray-200 dark:border-white/10' : ''}
        ${className}
      `}
    >
      {/* Breadcrumbs oberhalb des Titels */}
      {hasBreadcrumbs && <BreadcrumbRow items={breadcrumbs!} />}

      {/* Titel + Actions (mit Meta-Zeile unterhalb) */}
      <div
        className={`
          flex items-center gap-4 min-h-[40px]
          ${!centerActions ? 'justify-between' : ''}
        `}
      >
        <div className={`min-w-0 shrink-0 ${centerActions ? 'flex-1' : ''}`}>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{title}</h1>
          {description && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{description}</p>
          )}
          {hasMeta && <MetaRow items={meta!} />}
        </div>
        {actions && (
          <div className="flex items-center gap-3 shrink-0">{actions}</div>
        )}
        {/* Spacer für zentrierte Actions */}
        {centerActions && <div className="hidden sm:block sm:flex-1" />}
      </div>
    </div>
  );
}

// ============================================
// TWPageHeaderTabs - HEADER MIT TABS
// ============================================
export interface TWPageHeaderTabsProps {
  /** Seitentitel */
  title: string;
  /** Optionale Beschreibung */
  description?: string;
  /** Tabs für Navigation */
  tabs: Tab[];
  /** Callback wenn Tab gewechselt wird (für controlled mode) */
  onTabChange?: (tabId: string) => void;
  /** Optionale Aktions-Buttons (rechts vom Titel) */
  actions?: React.ReactNode;
  /** Optionale Breadcrumbs oberhalb des Titels */
  breadcrumbs?: Breadcrumb[];
  /** Optionale Meta-Info-Zeile unter dem Titel (Status, Datum, Autor mit Icons) */
  meta?: MetaItem[];
  /** Akzentfarbe für Tabs (Standard: orange) */
  accentColor?: 'orange' | 'gold';
  /** Extra CSS-Klassen */
  className?: string;
  /** Actions auf Mobile verstecken (Standard: false = immer sichtbar) */
  hideActionsOnMobile?: boolean;
  /** Actions horizontal zentrieren (Standard: false = rechts) */
  centerActions?: boolean;
}

export function TWPageHeaderTabs({
  title,
  description,
  tabs,
  onTabChange,
  actions,
  breadcrumbs,
  meta,
  accentColor = 'orange',
  className = '',
  hideActionsOnMobile = false,
  centerActions = false,
}: TWPageHeaderTabsProps) {
  const location = useLocation();

  // Farb-Klassen basierend auf accentColor
  const accentClasses = {
    orange: {
      border: 'border-orange-500',
      text: 'text-orange-500',
      ring: 'focus:ring-orange-500',
      badgeBg: 'bg-orange-500/10',
      badgeText: 'text-orange-500',
    },
    gold: {
      // Brand-Farbe (#F1C40F) - definiert in tailwind.config.ts
      // Underline-Stil: Gold-Unterstrich + kontrastreiche Textfarbe
      border: 'border-brand',
      text: 'text-gray-900 dark:text-brand',
      ring: 'focus:ring-brand',
      badgeBg: 'bg-brand/15 dark:bg-brand/10',
      badgeText: 'text-gray-900 dark:text-brand',
    },
  };

  const colors = accentClasses[accentColor];

  // Automatische Erkennung des aktiven Tabs basierend auf URL (falls href gesetzt)
  const tabsWithCurrent = tabs.map((tab) => ({
    ...tab,
    current: tab.current !== undefined ? tab.current : (tab.href ? location.pathname === tab.href : false),
  }));

  const currentTab = tabsWithCurrent.find((tab) => tab.current);
  const hasTabs = tabsWithCurrent.length > 0;

  const handleTabClick = (tab: Tab, e: React.MouseEvent) => {
    if (onTabChange) {
      e.preventDefault();
      onTabChange(tab.id);
    }
  };

  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;
  const hasMeta = meta && meta.length > 0;

  return (
    <div className={`pt-5 border-b border-gray-200 dark:border-white/10 ${hasTabs ? 'pb-0' : 'pb-5'} ${className}`}>
      {/* Breadcrumbs oberhalb des Titels */}
      {hasBreadcrumbs && <BreadcrumbRow items={breadcrumbs!} />}

      {/* Header mit Titel, Mobile-Dropdown und Actions */}
      <div className={`flex items-center gap-3 min-h-[40px] ${!centerActions ? 'justify-between' : ''}`}>
        <div className={`min-w-0 ${centerActions ? 'flex-1' : ''}`}>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{title}</h1>
          {description && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{description}</p>
          )}
          {hasMeta && <MetaRow items={meta!} />}
        </div>

        {/* Mobile: Dropdown neben Titel */}
        {hasTabs && (
          <div className="sm:hidden shrink-0">
            <Menu as="div" className="relative">
              <MenuButton className={`
                inline-flex items-center gap-x-1.5 rounded-lg
                bg-white dark:bg-white/5
                px-3 py-2
                text-sm font-medium text-gray-900 dark:text-white
                ring-1 ring-gray-200 dark:ring-white/10
                hover:bg-gray-50 dark:hover:bg-white/10
                focus:outline-none
                transition-colors
              `}>
                {currentTab?.icon && (
                  <currentTab.icon className={`w-4 h-4 ${colors.text}`} aria-hidden="true" />
                )}
                <span>{currentTab?.name || 'Auswählen'}</span>
                <ChevronDown className="w-4 h-4 text-gray-400 -mr-0.5" aria-hidden="true" />
              </MenuButton>

              <MenuItems
                transition
                anchor="bottom start"
                className="glass-surface min-w-[200px] origin-top-left rounded-2xl p-1 text-sm transition duration-100 ease-out [--anchor-gap:--spacing(2)] focus:outline-none data-closed:scale-95 data-closed:opacity-0 z-[10000]"
              >
                {tabsWithCurrent.map((tab) => {
                  const TabIcon = tab.icon;
                  return (
                    <MenuItem key={tab.id}>
                      <button
                        type="button"
                        onClick={() => {
                          if (onTabChange) {
                            onTabChange(tab.id);
                          } else if (tab.href) {
                            window.location.href = tab.href;
                          }
                        }}
                        className={`
                          group flex w-full items-center gap-3 rounded-lg px-3 py-2
                          data-focus:bg-gray-100 dark:data-focus:bg-white/10
                          cursor-pointer
                          ${tab.current
                            ? `${colors.text} font-medium`
                            : 'text-gray-700 dark:text-gray-300'
                          }
                        `}
                      >
                        {TabIcon && (
                          <TabIcon
                            className={`size-4 ${tab.current ? colors.text : 'text-gray-500 dark:text-white/60 group-data-focus:text-gray-900 dark:group-data-focus:text-white'}`}
                            aria-hidden="true"
                          />
                        )}
                        <span className="flex-1 text-left">{tab.name}</span>
                        {tab.current && (
                          <Check className={`size-4 ${colors.text}`} aria-hidden="true" />
                        )}
                      </button>
                    </MenuItem>
                  );
                })}
              </MenuItems>
            </Menu>
          </div>
        )}

        {actions && (
          <div className={`${hideActionsOnMobile ? 'hidden sm:flex' : 'flex'} items-center gap-3 shrink-0`}>
            {actions}
          </div>
        )}

        {/* Spacer für zentrierte Actions */}
        {centerActions && <div className="hidden sm:block flex-1" />}
      </div>

      {/* Tabs (Desktop) */}
      {hasTabs && (
        <div className="mt-4">

          {/* Desktop: Underline-Tabs (Tailwind-Plus Pattern 02) */}
          <nav aria-label="Tabs" className="hidden sm:flex -mb-px space-x-8">
            {tabsWithCurrent.map((tab) => {
              const TabIcon = tab.icon;

              const tabClassName = `
                group inline-flex items-center border-b-2 px-1 pb-4 text-sm font-medium whitespace-nowrap
                transition-colors duration-150
                ${tab.current
                  ? `${colors.border} ${colors.text}`
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-white/20 hover:text-gray-700 dark:hover:text-white'
                }
              `;

              const iconClassName = `
                -ml-0.5 mr-2 h-5 w-5
                ${tab.current
                  ? colors.text
                  : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'
                }
              `;

              const tabContent = (
                <>
                  {TabIcon && <TabIcon className={iconClassName} aria-hidden="true" />}
                  <span>{tab.name}</span>
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span className={`
                      ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-xs font-medium
                      ${tab.current
                        ? `${colors.badgeBg} ${colors.badgeText}`
                        : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-400'
                      }
                    `}>
                      {tab.badge}
                    </span>
                  )}
                </>
              );

              // Wenn href gesetzt ist, Link verwenden, sonst Button
              if (tab.href) {
                return (
                  <Link
                    key={tab.id}
                    to={tab.href}
                    onClick={(e) => onTabChange && handleTabClick(tab, e)}
                    aria-current={tab.current ? 'page' : undefined}
                    className={tabClassName}
                  >
                    {tabContent}
                  </Link>
                );
              }

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={(e) => handleTabClick(tab, e)}
                  aria-current={tab.current ? 'page' : undefined}
                  className={tabClassName}
                >
                  {tabContent}
                </button>
              );
            })}
          </nav>
        </div>
      )}
    </div>
  );
}

// ============================================
// LEGACY EXPORTS (für Rückwärtskompatibilität)
// ============================================

/** @deprecated Verwende TWPageHeader stattdessen */
export const TWSimplePageHeader = TWPageHeader;

/** @deprecated Verwende TWPageHeaderTabsProps stattdessen */
export type TWPageHeaderOldProps = TWPageHeaderTabsProps;

export default TWPageHeader;
